import React, { useMemo } from 'react';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { InputTextarea } from 'primereact/inputtextarea';
import { RadioButton } from 'primereact/radiobutton';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { useGetLmsCatalogPlanningsQuery } from '../../../../../api';
import LmsCodeDropdown from '../../../components/LmsCodeDropdown';

/**
 * Eğitim Raporu ekleme/düzenleme form alanları — sayfanın (`TrainingReportCreateEdit.jsx`) üst
 * kısmına gömülü, salt sunum katmanı bir form parçası (state ve kaydetme mantığı sayfada).
 * `TrainingCatalogFormFields.jsx` ile aynı desen: 3 sütunlu grid (`md:col-4`), `p-fluid`.
 * Bkz. docs/LMS-Egitim-Raporlari-API.md §2-3.
 */
const TrainingReportFormFields = ({ formData, setFormData, isFieldInvalid }) => {
    const { th } = useLanguage();

    const { data: planningsData, isFetching: isLoadingPlannings } = useGetLmsCatalogPlanningsQuery({ pageNumber: 1, pageSize: 100 });
    const planningOptions = useMemo(
        () =>
            (planningsData?.items || []).map((item) => ({
                value: item.id,
                label: [item.trainingName || item.trainingCode, item.startDate].filter(Boolean).join(' - ')
            })),
        [planningsData]
    );

    return (
        <div className="grid p-fluid">
            <div className="col-12 md:col-4">
                <div className="field">
                    <label htmlFor="reportCatalogPlanning">{th('trainingReport.fields.catalogPlanning')}</label>
                    <Dropdown
                        id="reportCatalogPlanning"
                        value={formData.catalogPlanningId}
                        options={planningOptions}
                        optionLabel="label"
                        optionValue="value"
                        onChange={(e) => setFormData((prev) => ({ ...prev, catalogPlanningId: e.value }))}
                        placeholder={th('trainingReport.fields.selectCatalogPlanningPlaceholder')}
                        loading={isLoadingPlannings}
                        filter
                        showClear
                    />
                </div>
            </div>

            <div className="col-12 md:col-4">
                <div className="field">
                    <label htmlFor="reportVehicleModel">
                        {th('trainingReport.fields.vehicleModel')} {!formData.catalogPlanningId && <span className="text-red-500">*</span>}
                    </label>
                    <LmsCodeDropdown
                        id="reportVehicleModel"
                        groupCode="VEHICLE_MODEL"
                        value={formData.vehicleModel}
                        onChange={(value) => setFormData((prev) => ({ ...prev, vehicleModel: value }))}
                        placeholder={th('trainingReport.fields.selectPlaceholder')}
                        invalid={isFieldInvalid('vehicleModel')}
                    />
                </div>
            </div>

            <div className="col-12 md:col-4">
                <div className="field">
                    <label htmlFor="reportTrainingType">
                        {th('trainingReport.fields.trainingType')} {!formData.catalogPlanningId && <span className="text-red-500">*</span>}
                    </label>
                    <LmsCodeDropdown
                        id="reportTrainingType"
                        groupCode="TRAINING_TYPES"
                        value={formData.trainingType}
                        onChange={(value) => setFormData((prev) => ({ ...prev, trainingType: value }))}
                        placeholder={th('trainingReport.fields.selectPlaceholder')}
                        invalid={isFieldInvalid('trainingType')}
                    />
                </div>
            </div>

            <div className="col-12 md:col-4">
                <div className="field">
                    <label htmlFor="reportPlanningLocation">{th('trainingReport.fields.planningLocation')}</label>
                    <LmsCodeDropdown
                        id="reportPlanningLocation"
                        groupCode="TRAINING_LOCATION"
                        value={formData.planningLocation}
                        onChange={(value) => setFormData((prev) => ({ ...prev, planningLocation: value }))}
                        placeholder={th('trainingReport.fields.selectPlaceholder')}
                    />
                </div>
            </div>

            <div className="col-12 md:col-4">
                <div className="field">
                    <label htmlFor="reportCountry">{th('trainingReport.fields.country')}</label>
                    <InputText id="reportCountry" value={formData.country} maxLength={50} onChange={(e) => setFormData((prev) => ({ ...prev, country: e.target.value }))} />
                </div>
            </div>

            <div className="col-12 md:col-4">
                <div className="field">
                    <label htmlFor="reportType">{th('trainingReport.fields.reportType')}</label>
                    <InputText id="reportType" value={formData.reportType} maxLength={100} onChange={(e) => setFormData((prev) => ({ ...prev, reportType: e.target.value }))} />
                </div>
            </div>

            <div className="col-12 md:col-4">
                <div className="field">
                    <label htmlFor="reportStartDate">{th('trainingReport.fields.trainingStartDate')}</label>
                    <Calendar id="reportStartDate" value={formData.trainingStartDate} onChange={(e) => setFormData((prev) => ({ ...prev, trainingStartDate: e.value }))} dateFormat="yy-mm-dd" showIcon className="w-full" />
                </div>
            </div>

            <div className="col-12 md:col-4">
                <div className="field">
                    <label htmlFor="reportEndDate">{th('trainingReport.fields.trainingEndDate')}</label>
                    <Calendar
                        id="reportEndDate"
                        value={formData.trainingEndDate}
                        onChange={(e) => setFormData((prev) => ({ ...prev, trainingEndDate: e.value }))}
                        dateFormat="yy-mm-dd"
                        showIcon
                        className="w-full"
                        minDate={formData.trainingStartDate || undefined}
                    />
                </div>
            </div>

            <div className="col-12 md:col-4">
                <div className="field">
                    <label htmlFor="reportGivenBy">{th('trainingReport.fields.givenBy')}</label>
                    <InputText id="reportGivenBy" value={formData.givenBy} maxLength={20} onChange={(e) => setFormData((prev) => ({ ...prev, givenBy: e.target.value }))} />
                </div>
            </div>

            <div className="col-12 md:col-4">
                <div className="field">
                    <label htmlFor="reportMonth">{th('trainingReport.fields.month')}</label>
                    <InputText id="reportMonth" value={formData.month} maxLength={50} onChange={(e) => setFormData((prev) => ({ ...prev, month: e.target.value }))} />
                </div>
            </div>

            <div className="col-12 md:col-4">
                <div className="field">
                    <label htmlFor="reportWeek">{th('trainingReport.fields.week')}</label>
                    <InputText id="reportWeek" value={formData.week} maxLength={50} onChange={(e) => setFormData((prev) => ({ ...prev, week: e.target.value }))} />
                </div>
            </div>

            <div className="col-12 md:col-4">
                <div className="field">
                    <label htmlFor="reportNumberOfParticipants">{th('trainingReport.fields.numberOfParticipants')}</label>
                    <InputNumber id="reportNumberOfParticipants" value={formData.numberOfParticipants} min={0} onValueChange={(e) => setFormData((prev) => ({ ...prev, numberOfParticipants: e.value }))} />
                </div>
            </div>

            <div className="col-12 md:col-4">
                <div className="field">
                    <label htmlFor="reportParticipantType">{th('trainingReport.fields.participantType')}</label>
                    <InputText id="reportParticipantType" value={formData.participantType} maxLength={50} onChange={(e) => setFormData((prev) => ({ ...prev, participantType: e.target.value }))} />
                </div>
            </div>

            <div className="col-12 md:col-4">
                <div className="field">
                    <label htmlFor="reportNumberOfDays">{th('trainingReport.fields.numberOfDays')}</label>
                    <InputNumber id="reportNumberOfDays" value={formData.numberOfDays} min={0} onValueChange={(e) => setFormData((prev) => ({ ...prev, numberOfDays: e.value }))} />
                </div>
            </div>

            <div className="col-12 md:col-4">
                <div className="field">
                    <label htmlFor="reportHour">{th('trainingReport.fields.hour')}</label>
                    <InputNumber id="reportHour" value={formData.hour} min={0} onValueChange={(e) => setFormData((prev) => ({ ...prev, hour: e.value }))} />
                </div>
            </div>

            <div className="col-12 md:col-4">
                <div className="field">
                    <label htmlFor="reportNumberOfTrainers">{th('trainingReport.fields.numberOfTrainers')}</label>
                    <InputNumber id="reportNumberOfTrainers" value={formData.numberOfTrainers} min={0} onValueChange={(e) => setFormData((prev) => ({ ...prev, numberOfTrainers: e.value }))} />
                </div>
            </div>

            <div className="col-12 md:col-4">
                <div className="field">
                    <label htmlFor="reportManHour">{th('trainingReport.fields.manHour')}</label>
                    <InputNumber id="reportManHour" value={formData.manHour} min={0} onValueChange={(e) => setFormData((prev) => ({ ...prev, manHour: e.value }))} />
                </div>
            </div>

            <div className="col-12 md:col-4">
                <div className="field">
                    <label htmlFor="reportEstimatedCostEur">{th('trainingReport.fields.estimatedCostEur')}</label>
                    <InputNumber id="reportEstimatedCostEur" value={formData.estimatedCostEur} min={0} mode="decimal" maxFractionDigits={4} onValueChange={(e) => setFormData((prev) => ({ ...prev, estimatedCostEur: e.value }))} />
                </div>
            </div>

            <div className="col-12 md:col-4">
                <div className="field">
                    <label htmlFor="reportCostSpentEur">{th('trainingReport.fields.costSpentEur')}</label>
                    <InputNumber id="reportCostSpentEur" value={formData.costSpentEur} min={0} mode="decimal" maxFractionDigits={4} onValueChange={(e) => setFormData((prev) => ({ ...prev, costSpentEur: e.value }))} />
                </div>
            </div>

            <div className="col-12 md:col-4">
                <div className="field">
                    <label htmlFor="reportEstimatedCostUsd">{th('trainingReport.fields.estimatedCostUsd')}</label>
                    <InputNumber id="reportEstimatedCostUsd" value={formData.estimatedCostUsd} min={0} mode="decimal" maxFractionDigits={4} onValueChange={(e) => setFormData((prev) => ({ ...prev, estimatedCostUsd: e.value }))} />
                </div>
            </div>

            <div className="col-12 md:col-4">
                <div className="field">
                    <label htmlFor="reportCostSpentUsd">{th('trainingReport.fields.costSpentUsd')}</label>
                    <InputNumber id="reportCostSpentUsd" value={formData.costSpentUsd} min={0} mode="decimal" maxFractionDigits={4} onValueChange={(e) => setFormData((prev) => ({ ...prev, costSpentUsd: e.value }))} />
                </div>
            </div>

            <div className="col-12 md:col-4">
                <div className="field">
                    <label htmlFor="reportEstimatedCostTry">{th('trainingReport.fields.estimatedCostTry')}</label>
                    <InputNumber id="reportEstimatedCostTry" value={formData.estimatedCostTry} min={0} mode="decimal" maxFractionDigits={4} onValueChange={(e) => setFormData((prev) => ({ ...prev, estimatedCostTry: e.value }))} />
                </div>
            </div>

            <div className="col-12 md:col-4">
                <div className="field">
                    <label htmlFor="reportCostSpentTry">{th('trainingReport.fields.costSpentTry')}</label>
                    <InputNumber id="reportCostSpentTry" value={formData.costSpentTry} min={0} mode="decimal" maxFractionDigits={4} onValueChange={(e) => setFormData((prev) => ({ ...prev, costSpentTry: e.value }))} />
                </div>
            </div>

            <div className="col-12 md:col-4">
                <div className="field">
                    <label>{th('common.status')}</label>
                    <div className="flex gap-4 mt-2">
                        <div className="flex align-items-center">
                            <RadioButton inputId="reportIsActiveYes" name="reportIsActive" value={1} onChange={(e) => setFormData((prev) => ({ ...prev, isActive: e.value }))} checked={Number(formData.isActive) === 1} />
                            <label htmlFor="reportIsActiveYes" className="ml-2 cursor-pointer">
                                {th('common.active', { fallback: 'Aktif' })}
                            </label>
                        </div>
                        <div className="flex align-items-center">
                            <RadioButton inputId="reportIsActiveNo" name="reportIsActive" value={0} onChange={(e) => setFormData((prev) => ({ ...prev, isActive: e.value }))} checked={Number(formData.isActive) === 0} />
                            <label htmlFor="reportIsActiveNo" className="ml-2 cursor-pointer">
                                {th('common.inactive', { fallback: 'Pasif' })}
                            </label>
                        </div>
                    </div>
                </div>
            </div>

            <div className="col-12">
                <div className="field">
                    <label htmlFor="reportDetail">{th('trainingReport.fields.detail')}</label>
                    <InputTextarea id="reportDetail" value={formData.detail} maxLength={150} rows={2} className="w-full" onChange={(e) => setFormData((prev) => ({ ...prev, detail: e.target.value }))} />
                </div>
            </div>
        </div>
    );
};

export default TrainingReportFormFields;
